
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { sharedState } from '../../../../framework/state';
import { getEnumerationOptions, getOptionLabel } from '../../../../framework/service/options';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../framework/client/resource';
import SelectField from '../../../../framework/fields/SelectField.vue';

import { VendorLocation } from '../../../../../common/application/model/VendorLocation';
import { VendorType } from '../../../../../common/application/model/enums/VendorType';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { getVendorOptions } from '../../../service/application_options_service';
import { FrameworkUserRole } from '../../../../../common/framework/enumeration/FrameworkUserRole';
import { ApplicationUserRole } from '../../../../../common/application/enumeration/ApplicationUserRole';
import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';
import { errorToast, successToast } from '../../../service/toast_service';
import ParameterService from "../../../service/parameter_service";

@Component({
    components: {
        SelectField,
        AutoCompleteMultipleField,
    },
})
export default class VendorLocations extends Vue {
    readonly resourceType = ApplicationResource.VENDOR_LOCATION;
    parameterService = new ParameterService(this.resourceType);

    @Prop(String) readonly parentId!: string;

    shared = sharedState;
    local = {
        rows: [] as VendorLocation[],
        total: 0,
        loading: false,
        selected: undefined as VendorLocation | undefined,
        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorOptions: [] as { id: string | undefined; label: string }[],
        parameters: this.parameterService.get(),
    };

    async mounted() {
        this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);
        this.local.vendorOptions = await getVendorOptions();
        await this.loadAsyncData();
    }

    async loadAsyncData() {
        this.local.loading = true;
        const backendParameters = this.parameterService.getBackendParameters();

        if (this.parentId) {
            backendParameters.set('parentId', this.parentId);
        }

        this.local.total =
            (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;

        this.local.rows = [];
        const rows = (await getResourcesNew(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        )) as Array<VendorLocation>;

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    async onSortChange(field: string, order: 'desc' | 'asc') {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    @Watch('local.parameters.vendorType')
    selectedVendorTypeChange() {
        if (this.local.loading) {
            return;
        }

        this.resetPagingAndLoadData();
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    add() {
        if (this.parentId) {
            this.$router.push(`/${this.resourceType}/add/${this.parentId}`);
        } else {
            this.$router.push(`/${this.resourceType}/add`);
        }
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    getVendorTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.vendorTypeOptions);
    }

    getFullAddress(row: VendorLocation): string {
        if (!row.street) {
            return '';
        }

        return `${row.street}, ${row.zip} ${row.city}`;
    }

    getVendorName(id: string): string {
        return getOptionLabel(id, this.local.vendorOptions);
    }

    view() {
        if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}`);
        }
    }

    onRowClick(row: VendorLocation) {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    get vendorIds(): string[] {
        return this.local.parameters.parentId || [];
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
